// Inherited permissions originate at folder/role; editing here would create
// a direct override silently. Force admin to navigate to source for clarity.

import { CornerDownRight, Lock, Trash2 } from "lucide-react";
import { cn } from "@/utils";
import Checkbox from "@/components/form/input/Checkbox";
import UserCell from "@/components/users/user-cell";
import { formatUserName } from "@/utils/format-user-name";
import type { ActionDef } from "./resource-permission-matrix";
import type { PermissionEntry, PrincipalKind } from "@/hooks/api/mutations/permissions.mutations";

export interface PermRow extends PermissionEntry {
  checked: Record<string, boolean>;
}

interface PermissionMatrixRowProps {
  row: PermRow;
  rowIdx: number;
  actions: ActionDef[];
  isInherited: boolean;
  onCheck: (rowIdx: number, actionKey: string, checked: boolean) => void;
  onRemove: (row: PermRow) => void;
  isActionPending?: (actionKey: string) => boolean;
  isRemoveDisabled?: boolean;
  // Translator fn passed from parent — avoids re-exporting hook boundary.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: any, values?: Record<string, string>) => string;
}

export function PermissionMatrixRow({
  row,
  rowIdx,
  actions,
  isInherited,
  onCheck,
  onRemove,
  isActionPending,
  isRemoveDisabled,
  t,
}: PermissionMatrixRowProps) {
  const fakeUser =
    row.principalKind === "user"
      ? {
          id: row.principalId ?? "",
          firstName: null,
          lastName: null,
          email: row.principalLabel ?? null,
          username: null,
          avatarUrl: null,
        }
      : null;

  const principalLabel =
    row.principalKind === "user"
      ? formatUserName(row.principalUser ?? fakeUser, { fallback: t("share.unknownUser") })
      : row.principalLabel ??
        (row.principalKind === "role"
          ? `${t("principal.role")}: ${row.principalRole ?? t("share.unknownPrincipal")}`
          : `${t("principal.group")}: ${t("share.unknownPrincipal")}`);

  const inheritedTooltip = row.sourceName
    ? t("inheritedFromTitle", { folder: row.sourceName })
    : undefined;

  return (
    <tr className={cn("group", isInherited && "opacity-60")}>
      <td className="py-2.5 pr-3">
        <div className="flex items-center gap-2">
          {isInherited && (
            <CornerDownRight
              size={12}
              className="shrink-0 text-[var(--muted-foreground)]"
              aria-hidden
            />
          )}
          {row.principalKind === "user" ? (
            <UserCell
              user={row.principalUser ?? fakeUser}
              size="sm"
              fallbackText={t("share.unknownUser")}
            />
          ) : (
            <span className="text-[13px] text-[var(--foreground)]">{principalLabel}</span>
          )}
          {isInherited && inheritedTooltip && (
            <span title={inheritedTooltip} className="ml-1 cursor-help">
              <Lock size={10} className="text-[var(--muted-foreground)]" aria-hidden />
            </span>
          )}
        </div>
      </td>

      {actions.map((a) => (
        <td key={a.key} className="py-2.5 px-2 text-center">
          <Checkbox
            checked={row.checked[a.key] ?? false}
            disabled={isInherited || isActionPending?.(a.key)}
            onChange={(checked) => !isInherited && onCheck(rowIdx, a.key, checked)}
            className="mx-auto"
            aria-label={`${a.label} for ${principalLabel}`}
          />
        </td>
      ))}

      <td className="py-2.5 text-right">
        {!isInherited && (
          <button
            type="button"
            onClick={() => onRemove(row)}
            disabled={isRemoveDisabled}
            className="rounded p-1 text-[var(--muted-foreground)] hover:bg-[var(--destructive-bg)] hover:text-[var(--destructive)] opacity-0 group-hover:opacity-100 transition-opacity duration-[120ms]"
            aria-label={t("share.removeAccess")}
          >
            <Trash2 size={13} aria-hidden />
          </button>
        )}
      </td>
    </tr>
  );
}

// Re-export PrincipalKind for convenience of callers importing from this module.
export type { PrincipalKind };
